import { toast } from 'react-toastify'
import type { ErrorRoot } from '../../../types'

/** Tvar odpovědi vygenerovaného klienta (@hey-api/client-fetch). */
type ApiResult<T> = {
    data?: T
    error?: unknown
    response: Response
}

export type ApiFailure = {
    code: string
    message: string
    details?: Record<string, unknown>
    status: number
}

export type ActionApiCallOptions = {
    /** Hláška zobrazená po úspěchu; bez ní se nic nezobrazuje. */
    successMessage?: string
    /** Potlačí automatické zobrazení chyby (volající si ji ošetří sám). */
    silent?: boolean
    /** Vlastní reakce na chybu; vrací true, pokud už chybu zobrazil. */
    onError?: (failure: ApiFailure) => boolean | void
}

function toFailure(result: ApiResult<unknown>): ApiFailure {
    const body = result.error as Partial<ErrorRoot> | undefined
    return {
        code: body?.code ?? 'NETWORK_ERROR',
        message: body?.message ?? 'Server je nedostupný. Zkontrolujte připojení a zkuste to znovu.',
        details: body?.details,
        status: result.response?.status ?? 0,
    }
}

/**
 * Jediný povolený způsob volání generovaného API z komponent.
 *
 * Sjednocuje zobrazení chyb (toast), takže komponenty nemusí psát try/catch,
 * a vrací `undefined`, pokud volání selhalo.
 */
export async function actionApiCall<T>(call: () => Promise<ApiResult<T>>, options: ActionApiCallOptions = {}): Promise<T | undefined> {
    let result: ApiResult<T>

    try {
        result = await call()
    } catch (err) {
        // Sem se dostaneme jen při selhání sítě - klient sám výjimky nevyhazuje.
        const failure: ApiFailure = {
            code: 'NETWORK_ERROR',
            message: 'Server je nedostupný. Zkontrolujte připojení a zkuste to znovu.',
            status: 0,
        }
        if (!options.silent && options.onError?.(failure) !== true) {
            toast.error(failure.message)
        }
        console.error('Volání API selhalo', err)
        return undefined
    }

    if (result.error !== undefined || !result.response.ok) {
        const failure = toFailure(result)
        const handled = options.onError?.(failure) === true
        if (!options.silent && !handled) {
            toast.error(failure.message)
        }
        return undefined
    }

    if (options.successMessage) {
        toast.success(options.successMessage)
    }

    return result.data
}

/**
 * Varianta pro odesílání formulářů. Vrací chyby jednotlivých polí, aby je
 * formulář mohl zobrazit u příslušných inputů; obecnou chybu řeší toastem.
 */
export async function formSubmit<T>(
    call: () => Promise<ApiResult<T>>,
    options: ActionApiCallOptions = {},
): Promise<{ data?: T; fieldErrors?: Record<string, string> }> {
    let fieldErrors: Record<string, string> | undefined

    const data = await actionApiCall(call, {
        ...options,
        onError: (failure) => {
            const fields = failure.details?.fields
            if (fields && typeof fields === 'object') {
                fieldErrors = fields as Record<string, string>
                return true // chyby polí zobrazí formulář, toast není potřeba
            }
            return options.onError?.(failure)
        },
    })

    return { data, fieldErrors }
}
